import { z } from 'zod';
import { MAX_RETENTION_WINDOW_HOURS, MIN_RETENTION_WINDOW_HOURS, } from '../../types/dataRetention';
import assertUnreachable from '../../utils/assertUnreachable';
import { ToolNameSchema } from './schemas/visualizations';
export * from './adminTypes';
export * from './aiEvalAssessment';
export * from './canonicalizeAiMerge';
export * from './chartConfig/slack';
export * from './chartConfig/web';
export * from './constants';
export * from './coder';
export * from './composerArtifact';
export * from './composerViz';
export * from './composerVizPanel';
export * from './dashboardContext';
export * from './dashboardLayout';
export * from './aiAgentReviewClassifierTypes';
export * from './documentGuidance';
export * from './documentTypes';
export * from './skillTypes';
export * from './threadFileTypes';
export * from './skillValidation';
export * from './filterExploreByTags';
export * from './projectContext';
export * from './requestTypes';
export * from './schemas';
export * from './schemas/agentReadiness';
export * from './schemas/agentSuggestions';
export * from './types';
export * from './utils';
export * from './validators';
export const baseAgentSchema = z.object({
    uuid: z.string(),
    projectUuid: z.string(),
    organizationUuid: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    imageUrl: z.string().url().nullable(),
    imageUrlSource: z.enum(['upload', 'url']).nullable(),
    tags: z.array(z.string()).nullable(),
    integrations: z.array(z.object({
        type: z.literal('slack'),
        channelId: z.string(),
    })),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    instruction: z
        .string()
        .max(8192, // 8kb
    'Custom instruction is too long. Maximum allowed is 8,100 characters.')
        .nullable(),
    provider: z.string(),
    model: z.string(),
    groupAccess: z.array(z.string()),
    userAccess: z.array(z.string()),
    spaceAccess: z.array(z.string()),
    enableDataAccess: z.boolean(),
    enableSelfImprovement: z.boolean(),
    enableContentTools: z.boolean(),
    enableUserContext: z.boolean(),
    enableSqlMode: z.boolean(),
    adminOnly: z.boolean(),
    modelConfig: z.custom().nullable(),
    // AI provider credential this agent's prompts run on. Null means the
    // project or organization selection applies.
    providerCredentialUuid: z.string().nullable(),
    version: z.number(),
    threadRetentionHours: z
        .number()
        .int()
        .min(MIN_RETENTION_WINDOW_HOURS)
        .max(MAX_RETENTION_WINDOW_HOURS)
        .nullable(),
});
// An empty spaceAccess list means the agent is unrestricted (all spaces).
export const isSpaceRestrictedAgent = (agent) => agent.spaceAccess.length > 0;
export const hasAiAgentAccessToSpace = (agent, spaceUuid) => !isSpaceRestrictedAgent(agent) || agent.spaceAccess.includes(spaceUuid);
export const AI_AGENT_MEMORY_CONSOLIDATION_OPERATION_TYPES = ['merge', 'promote', 'supersede', 'retire'];
export const AI_AGENT_MEMORY_PROMOTION_MIN_CITED_COUNT = 10;
export const hasSufficientPromotionCitations = (citedCount) => citedCount >= AI_AGENT_MEMORY_PROMOTION_MIN_CITED_COUNT;
/**
 * Every existing row an operation names — a merge's sources included. Validation
 * and the apply transaction's locking share it, so the set of rows an operation
 * is checked against can never drift from the set it locks.
 */
export const getAiAgentMemoryConsolidationOperationSlugs = (operation) => {
    switch (operation.type) {
        case 'merge':
            return operation.source_slugs;
        case 'promote':
            return [operation.slug];
        case 'supersede':
            return [operation.loser_slug, operation.winner_slug];
        case 'retire':
            return [operation.slug];
        default:
            return assertUnreachable(operation, 'Unknown consolidation op');
    }
};
/** Enumerable and content-free, so both the run audit and metrics can label by it. */
export const AI_AGENT_MEMORY_CONSOLIDATION_REJECTION_REASONS = [
    'unknown_slug',
    'duplicate_target',
    'self_supersede',
    'insufficient_sources',
    'insufficient_citations',
    'promotion_conflict',
    'promotion_failed',
    'row_moved',
];
/** Identifies the path that started a consolidation run. */
export const AI_AGENT_MEMORY_CONSOLIDATION_TRIGGERS = [
    'scheduled',
    'manual',
];
// The hosted GitHub MCP server. The one-click "Connect GitHub" flow points the
// MCP server here and authenticates with the org's GitHub App installation
// token (minted server-side), so no separate OAuth/PAT step is needed.
export const GITHUB_MCP_SERVER_URL = 'https://api.githubcopilot.com/mcp/';
export const GITHUB_MCP_SERVER_NAME = 'GitHub';
// Hostname match rather than exact URL so trailing-slash or path variants
// can't sidestep checks that gate GitHub MCP credentials.
export const isGithubMcpServerUrl = (url) => {
    try {
        return (new URL(url).hostname === new URL(GITHUB_MCP_SERVER_URL).hostname);
    }
    catch {
        return false;
    }
};
export const AI_AGENT_THREAD_TITLE_MAX_LENGTH = 200;
export const isAiAgentSemanticArtifactVizQuery = (query) => query.source === 'semantic';
export const isAiAgentSqlArtifactVizQuery = (query) => query.source === 'sql';
export const isAiAgentComposerArtifactVizQuery = (query) => query.source === 'composer';
export const isAiAgentMcpToolName = (toolName) => toolName.startsWith('mcp_');
export const isAiAgentToolName = (toolName) => ToolNameSchema.safeParse(toolName).success ||
    isAiAgentMcpToolName(toolName);
export const isAiSqlChartArtifactConfig = (config) => typeof config === 'object' &&
    config !== null &&
    'source' in config &&
    config.source === 'sql' &&
    'sql' in config &&
    typeof config.sql === 'string' &&
    'limit' in config &&
    typeof config.limit === 'number';
export const isAiMergeChartArtifactConfig = (config) => typeof config === 'object' &&
    config !== null &&
    'source' in config &&
    config.source === 'merge' &&
    'schemaVersion' in config &&
    config.schemaVersion === 1 &&
    'config' in config &&
    typeof config.config === 'object' &&
    config.config !== null;
/**
 * Type guard to check if a CreateEvaluationPrompt is a string prompt
 */
export const isStringPrompt = (prompt) => 'prompt' in prompt;
/**
 * Type guard to check if a CreateEvaluationPrompt is a thread reference
 */
export const isThreadPrompt = (prompt) => 'promptUuid' in prompt && 'threadUuid' in prompt;
//# sourceMappingURL=index.js.map